import { playSound } from 'flow/actions/sounds-actions';
import { getFaro } from 'flow/api-clients/faro-client';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import AppContext from 'flow/contexts/app-context';
import {
    addGeneralLogEntry,
    errorMessageIsPlayRequestInterrupted,
} from 'flow/utils/browser-utils';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

class BaseAudioPlayer extends React.PureComponent {
    static propTypes = {
        url: PropTypes.string.isRequired,
        downloadUrl: PropTypes.string,
        duration: PropTypes.number,
        downloadable: PropTypes.bool,
        uniqueId: PropTypes.string, //voicemail uniqueId
        onPlay: PropTypes.func,
        playSound: PropTypes.func,
    };

    static contextType = AppContext;

    static defaultProps = {
        duration: 0,
    };

    constructor(props) {
        super(props);

        /* Set props.duration for DTO files related to voicemails and recorded calls since we can't rely on
         * the Content-Length in the header request.
         */
        this.state = {
            isPlaying: false,
            onPlayerHead: false,
            duration: props.duration,
            leftPosition: 0,
            currentTime: 0,
        };
    }

    play = (event) => {
        const { duration } = this.state;
        const { currentTime } = this._audio;
        const { playSound, onPlay } = this.props;
        playSound?.(this.props.uniqueId);

        event?.stopPropagation();
        for (const pauseButton of document.querySelectorAll(
            '.audio-player-pause',
        )) {
            pauseButton.click();
        }

        if (currentTime >= duration) {
            this.updateCurrentTime(0);
        }

        const playPromise = this._audio.play();

        if (playPromise instanceof Promise) {
            playPromise
                .then(() => {
                    this.setState({ isPlaying: true });
                    if (onPlay) {
                        onPlay();
                    }
                })
                .catch((error) => {
                    //Catching promise error to silence following error:
                    //"AbortError: The play() request was interrupted by a call to pause(). https://goo.gl/LdLk22"
                    console.error(error);
                    if (errorMessageIsPlayRequestInterrupted(error)) {
                        return;
                    }
                    getFaro()?.api.pushError(error);
                    addGeneralLogEntry(`Error reported: ${error}`);
                });
        } else {
            this.setState({ isPlaying: true });
            if (onPlay) {
                onPlay();
            }
        }
    };

    pause = (event) => {
        event?.stopPropagation();
        this._audio.pause();
        this.setState({ isPlaying: false });
    };

    stop = (event) => {
        event?.stopPropagation();

        this._audio.pause();
        this.updateCurrentTime(0);
        this.setState({ isPlaying: false });
    };

    updateCurrentTime = (value) => {
        this._audio.currentTime = value;
    };

    setDuration = () => {
        this.setState({ duration: this._audio.duration });
    };
}

class AudioPlayer extends BaseAudioPlayer {
    static propTypes = {
        handleMarkAsHandled: PropTypes.func,
        handleUnmarkAsHandled: PropTypes.func,
        className: PropTypes.string,
        onPlay: PropTypes.func,
    };

    static contextType = AppContext;

    componentDidMount() {
        this._audio.addEventListener('timeupdate', this.timeUpdate, false);
        this._progressBar.addEventListener('click', this.updateTimeline, false);
        this._playerHead.addEventListener(
            'mousedown',
            this.isMovingPlayerHead,
            false,
        );
        window.addEventListener('resize', this.timeUpdate, false);
        window.addEventListener('mouseup', this.stoppedMovingPlayerHead, false);
    }

    componentWillUnmount() {
        this._audio.removeEventListener('timeupdate', this.timeUpdate, false);
        this._progressBar.removeEventListener(
            'click',
            this.updateTimeline,
            false,
        );
        this._playerHead.removeEventListener(
            'mousedown',
            this.isMovingPlayerHead,
            false,
        );
        window.removeEventListener('resize', this.timeUpdate, false);
        window.removeEventListener(
            'mouseup',
            this.stoppedMovingPlayerHead,
            false,
        );
    }

    timeUpdate = () => {
        const { duration } = this.state;
        const { currentTime } = this._audio;

        const timelineWidth = this.getTimelineWidth();
        const positionFromLeft = timelineWidth * (currentTime / duration);
        this.setPlayerHeadPosition(positionFromLeft);

        if (currentTime >= duration) {
            this.pause();
        }
    };

    getTimelineWidth = () => {
        const timelineOffsets = this._timeLine.getBoundingClientRect();
        const playerHeadOffsets = this._playerHead.getBoundingClientRect();
        return timelineOffsets.width - playerHeadOffsets.width;
    };

    setPlayerHeadPosition = (pixelsFromLeft) => {
        const timelineWidth = this.getTimelineWidth();
        const newPosition = (() => {
            if (pixelsFromLeft < 0) {
                return 0;
            } else if (pixelsFromLeft > timelineWidth) {
                return timelineWidth;
            } else {
                return pixelsFromLeft;
            }
        })();
        this.setState({ leftPosition: newPosition });
    };

    updateTimeline = (e) => {
        this.movePlayerHead(e);
        this.updateCurrentTime(this.state.duration * this.clickPercent(e));
    };

    clickPercent = (e) => {
        const timelineOffsets = this._timeLine.getBoundingClientRect();
        const timelineWidth = this.getTimelineWidth();

        return (e.pageX - timelineOffsets.left) / timelineWidth;
    };

    movePlayerHead = (e) => {
        const timelineOffsets = this._timeLine.getBoundingClientRect();
        const pixelsFromLeft = e.pageX - timelineOffsets.left;

        this.setPlayerHeadPosition(pixelsFromLeft);
    };

    isMovingPlayerHead = () => {
        this.setOnPlayerHead(true);
        window.addEventListener('mousemove', this.movePlayerHead, true);
        this._audio.removeEventListener('timeupdate', this.timeUpdate, false);
    };

    stoppedMovingPlayerHead = (e) => {
        const { onPlayerHead, duration } = this.state;

        if (onPlayerHead) {
            this.movePlayerHead(e);
            window.removeEventListener('mousemove', this.movePlayerHead, true);
            this.updateCurrentTime(duration * this.clickPercent(e));
            this._audio.addEventListener('timeupdate', this.timeUpdate, false);
        }
        this.setOnPlayerHead(false);
    };

    setOnPlayerHead = (stateValue) => {
        this.setState({ onPlayerHead: stateValue });
    };

    presentCurrentTime = () => {
        const currentTime = parseInt(this._audio ? this._audio.currentTime : 0);
        const duration = parseInt(this.state.duration);

        const diff = duration - currentTime;
        const date = moment('1970-01-01 00:00:00');

        const diffDuration = moment.duration(diff * 1000);
        const diffMoment = date.add(diffDuration);
        const hours = diffDuration.get('hours');

        return diffMoment.format(hours > 0 ? 'HH:mm:ss' : 'mm:ss');
    };

    render() {
        const { url, className } = this.props;
        const { leftPosition, isPlaying } = this.state;
        const { i18next } = this.context;

        return (
            <>
                <audio ref={(ref) => (this._audio = ref)} preload="none">
                    <source src={url} />
                </audio>
                <div className="flex grow items-center gap-1">
                    <div
                        className={twMerge(
                            'flex items-center rounded-sm bg-gray-200',
                            className,
                        )}
                    >
                        {isPlaying ? (
                            <IconButton
                                aria-label={i18next.t('action.pause')}
                                className="audio-player-pause"
                                onClick={this.pause}
                            >
                                <Icon
                                    iconKey="pause"
                                    className="text-gray-600"
                                />
                            </IconButton>
                        ) : (
                            <IconButton
                                onClick={this.play}
                                tooltipLabel={i18next.t(
                                    i18next.t('Tooltip.Audio.Play'),
                                )}
                                className="rounded-sm"
                            >
                                <Icon
                                    iconKey="play_arrow"
                                    className="text-gray-600"
                                />
                            </IconButton>
                        )}
                    </div>

                    <div
                        className={twMerge(
                            'flex grow items-center gap-x-2 rounded-sm bg-gray-200 p-2',
                            className,
                        )}
                    >
                        <div
                            className="relative flex grow cursor-pointer items-center"
                            ref={(ref) => (this._progressBar = ref)}
                        >
                            <div
                                className={twMerge(
                                    'absolute h-1 w-full rounded-xs bg-gray-300',
                                    className,
                                )}
                                ref={(ref) => (this._timeLine = ref)}
                            />
                            <div
                                className={twMerge(
                                    'absolute h-1 rounded-xs bg-gray-800',
                                    className,
                                )}
                                style={{ width: leftPosition }}
                            />

                            <div ref={(ref) => (this._playerHead = ref)} />
                        </div>
                        <div className="shrink-0 text-center text-xs text-gray-500 tabular-nums">
                            {this.presentCurrentTime()}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

class MiniAudioPlayer extends BaseAudioPlayer {
    componentDidUpdate(prevProps) {
        if (prevProps.uniqueId !== this.props.uniqueId) {
            this.stop();
            this._audio.load();
        }
    }

    componentDidMount() {
        this._audio.addEventListener('timeupdate', this.timeUpdate, false);
        this._audio.addEventListener('durationchange', this.setDuration, false);
    }

    componentWillUnmount() {
        this._audio.removeEventListener('timeupdate', this.timeUpdate, false);
        this._audio.removeEventListener(
            'durationchange',
            this.setDuration,
            false,
        );
    }

    timeUpdate = () => {
        const { duration } = this.state;
        const { currentTime } = this._audio;

        if (currentTime >= duration) {
            this.stop();
        }
    };

    render() {
        const { url } = this.props;
        const { isPlaying } = this.state;
        const { i18next } = this.context;

        if (
            this._audio &&
            this.props.uniqueId &&
            this.props.uniqueId !== this.props.activeAudio
        ) {
            this.stop();
        }

        return (
            <>
                <audio ref={(ref) => (this._audio = ref)} preload="none">
                    <source src={url} />
                </audio>
                <div>
                    {isPlaying ? (
                        <IconButton
                            onClick={this.stop}
                            className="size-10"
                            aria-label={i18next.t('Tooltip.Title.Stop')}
                        >
                            <Icon iconKey="stop" className="text-red" />
                        </IconButton>
                    ) : (
                        <IconButton
                            onClick={this.play}
                            tooltipLabel={i18next.t('Tooltip.Audio.Play')}
                            className="text-red size-10"
                        >
                            <Icon
                                iconKey="play_arrow"
                                className="text-gray-500"
                            />
                        </IconButton>
                    )}
                </div>
            </>
        );
    }
}

const AudioPlayerComponent = AudioPlayer;
const MiniAudioPlayerComponent = MiniAudioPlayer;

const mapStateToProps = (state) => {
    return { activeAudio: state.sounds.get('playSound') };
};

const mapDispatchToProps = (dispatch) => {
    return {
        playSound: (id) => {
            dispatch(playSound(id));
        },
    };
};

const connectedMiniAudioPlayerComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
)(MiniAudioPlayerComponent);

export {
    AudioPlayerComponent as AudioPlayer,
    connectedMiniAudioPlayerComponent as MiniAudioPlayer,
};
